/**
 * Página Generar Oferta - Generador de Ofertas
 *
 * Permite subir PDFs de pliegos de licitación y generar automáticamente
 * la estructura de la memoria técnica de la oferta.
 * Sigue el mismo patrón UX que la página Analizar.
 */

import React, { useState, useRef, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  LinearProgress,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import FileUpload from '../components/FileUpload';
import { generateProposal, checkProposalStatus, downloadProposalWord, downloadFile } from '../services/api';
import { COLORS } from '../theme/theme';

// Fases del proceso con duración estimada (en segundos) y peso en el progreso
const PHASES = [
  { message: 'Subiendo documentos al servidor...', duration: 5, start: 0, end: 8 },
  { message: 'Extrayendo texto de los PDFs...', duration: 15, start: 8, end: 20 },
  { message: 'Dividiendo el documento en fragmentos...', duration: 10, start: 20, end: 30 },
  { message: 'Analizando estructura del pliego...', duration: 20, start: 30, end: 45 },
  { message: 'Detectando criterios de adjudicación...', duration: 20, start: 45, end: 60 },
  { message: 'Identificando requisitos de formato...', duration: 15, start: 60, end: 72 },
  { message: 'Generando estructura de la oferta...', duration: 25, start: 72, end: 88 },
  { message: 'Creando borrador del documento...', duration: 15, start: 88, end: 95 },
  { message: 'Finalizando y generando Word...', duration: 10, start: 95, end: 99 },
];

const GenerarPropuesta = () => {
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Refs para controlar los intervalos
  const pollingRef = useRef(null);
  const smoothRef = useRef(null);
  const timeoutRef = useRef(null);
  const startTimeRef = useRef(null);

  // Limpiar todos los intervalos/timeouts
  const clearAllTimers = useCallback(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (smoothRef.current) clearInterval(smoothRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    pollingRef.current = null;
    smoothRef.current = null;
    timeoutRef.current = null;
  }, []);

  // Calcular la fase actual y el progreso simulado según el tiempo transcurrido
  const getSimulatedProgress = useCallback(() => {
    if (!startTimeRef.current) return { progress: 0, message: PHASES[0].message };

    const elapsed = (Date.now() - startTimeRef.current) / 1000; // segundos transcurridos
    let accumulated = 0;

    for (const phase of PHASES) {
      if (elapsed < accumulated + phase.duration) {
        // Estamos en esta fase
        const phaseElapsed = elapsed - accumulated;
        const phaseProgress = phaseElapsed / phase.duration;
        const currentProgress = phase.start + (phase.end - phase.start) * phaseProgress;
        return { progress: Math.round(currentProgress), message: phase.message };
      }
      accumulated += phase.duration;
    }

    // Si pasamos todas las fases, quedarnos en 99%
    return { progress: 99, message: 'Finalizando...' };
  }, []);

  const handleFilesChange = (newFiles) => {
    setFiles(newFiles);
    setError('');
    setSuccess('');
  };

  const handleGenerate = async () => {
    if (files.length === 0) {
      setError('Por favor, selecciona al menos un archivo PDF');
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setError('');
    setSuccess('');
    setResult(null);
    setCurrentMessage('Iniciando generación de oferta...');
    startTimeRef.current = Date.now();

    // Progreso fluido: actualizar cada 500ms según tiempo transcurrido
    smoothRef.current = setInterval(() => {
      const simulated = getSimulatedProgress();
      setProgress(simulated.progress);
      setCurrentMessage(simulated.message);
    }, 500);

    try {
      const response = await generateProposal(files);
      const job_id = response.job_id;

      if (!job_id) {
        throw new Error('No se recibió un ID de trabajo del servidor');
      }

      // Polling al backend para detectar cuando termina
      pollingRef.current = setInterval(async () => {
        try {
          const status = await checkProposalStatus(job_id);

          if (status.status === 'completed') {
            clearAllTimers();
            setProgress(100);
            setCurrentMessage('¡Oferta generada!');

            if (status.result) {
              setResult(status.result);
              setSuccess('Oferta generada correctamente. Revisa el borrador a continuación.');
            } else {
              setError('La generación se completó pero no se recibieron datos.');
            }

            setIsProcessing(false);
          } else if (status.status === 'error') {
            clearAllTimers();
            setError(status.error || status.message || 'Error al procesar la oferta');
            setIsProcessing(false);
          }
        } catch (pollError) {
          console.error('Error en polling:', pollError);
          clearAllTimers();
          setError('Error al consultar el estado de la generación.');
          setIsProcessing(false);
        }
      }, 3000);

      // Timeout de seguridad: 20 minutos
      timeoutRef.current = setTimeout(() => {
        clearAllTimers();
        setError('La generación está tardando demasiado. Por favor, inténtalo de nuevo.');
        setIsProcessing(false);
      }, 1200000);

    } catch (err) {
      console.error('Error al iniciar generación:', err);
      clearAllTimers();
      setError(err.message || 'Error al generar la oferta');
      setIsProcessing(false);
    }
  };

  const handleDownloadWord = async () => {
    if (!result?.docx_filename) {
      setError('No hay archivo Word disponible para descargar');
      return;
    }

    try {
      const blob = await downloadProposalWord(result.docx_filename);
      downloadFile(blob, result.docx_filename);
      setSuccess('Documento Word descargado correctamente');
    } catch (err) {
      setError(err.message || 'Error al descargar el documento Word');
    }
  };

  return (
    <Container maxWidth="lg">
      {/* Encabezado */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Generador de Ofertas con IA
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Utiliza <strong>OpenAI</strong> y <strong>LangGraph</strong> para
          analizar los pliegos de la licitación, detectar los criterios de adjudicación y generar
          automáticamente la estructura de la memoria técnica de tu oferta.
        </Typography>
      </Box>

      {/* Paso 1: Subida de archivos */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Paso 1: Subir Documentos del Pliego
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Selecciona los archivos PDF del pliego (PCAP, PPT, Anexos)
        </Typography>

        <FileUpload
          onFilesChange={handleFilesChange}
          acceptedTypes=".pdf"
          multiple={true}
          label="Arrastra archivos PDF aquí o haz clic para seleccionar"
        />
      </Paper>

      {/* Boton Generar */}
      {files.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
            onClick={handleGenerate}
            disabled={isProcessing}
            fullWidth
          >
            {isProcessing ? 'Generando Oferta...' : 'Generar Oferta'}
          </Button>
        </Box>
      )}

      {/* Mensajes de error/exito */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Paso 2: Progreso */}
      {isProcessing && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Progreso de la Generación de la Oferta
          </Typography>

          <Box sx={{
            mb: 3,
            p: 2,
            backgroundColor: COLORS.backgroundLight,
            borderRadius: 2,
            borderLeft: `4px solid ${COLORS.teal}`
          }}>
            <Typography variant="body1" sx={{ fontWeight: 500, color: COLORS.teal, mb: 0.5 }}>
              {currentMessage || 'Iniciando generación de oferta...'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              La generación puede tardar varios minutos dependiendo del tamaño de los documentos
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Progreso general
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.teal }}>
                {progress}% completado
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 12,
                borderRadius: 6,
                backgroundColor: COLORS.backgroundLight,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: COLORS.teal,
                  borderRadius: 6,
                  transition: 'transform 0.5s ease',
                },
              }}
            />
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
            El proceso completo suele tardar entre 2 y 4 minutos
          </Typography>
        </Paper>
      )}

      {/* Paso 3: Resultados */}
      {result && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Borrador de Oferta
            </Typography>
            <Chip
              icon={<CheckCircleIcon />}
              label="Generación de Oferta Completada"
              color="success"
              size="medium"
            />
          </Box>

          {/* Scoring index content */}
          {result.scoring_index_found && result.scoring_index_content && (
            <Box sx={{
              mb: 3,
              p: 2,
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: 2,
            }}>
              <Typography variant="h6" sx={{ mb: 1, color: '#166534' }}>
                Criterios de Adjudicación Detectados
              </Typography>
              <Typography
                variant="body2"
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  color: '#15803d',
                  m: 0,
                }}
              >
                {result.scoring_index_content}
              </Typography>
            </Box>
          )}

          {/* Borrador en Markdown */}
          {result.proposal_draft && (
            <Box sx={{
              mb: 3,
              p: 3,
              backgroundColor: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: 2,
              maxHeight: 600,
              overflow: 'auto',
              '& h1': { fontSize: '1.5rem', fontWeight: 700, color: COLORS.navy, mt: 3, mb: 1, textTransform: 'none', letterSpacing: 'normal' },
              '& h2': { fontSize: '1.25rem', fontWeight: 600, color: COLORS.teal, mt: 2.5, mb: 1, textTransform: 'none', letterSpacing: 'normal' },
              '& h3': { fontSize: '1.1rem', fontWeight: 600, color: COLORS.textPrimary, mt: 2, mb: 0.5, textTransform: 'none', letterSpacing: 'normal' },
              '& h4, & h5, & h6': { fontSize: '1rem', fontWeight: 600, color: COLORS.textPrimary, mt: 1.5, mb: 0.5, textTransform: 'none', letterSpacing: 'normal' },
              '& p': { fontSize: '0.95rem', lineHeight: 1.7, color: COLORS.textPrimary, mb: 1 },
              '& ul, & ol': { pl: 2.5, mb: 1 },
              '& li': { fontSize: '0.95rem', lineHeight: 1.7, color: COLORS.textPrimary, mb: 0.3 },
              '& code': { backgroundColor: '#f1f5f9', px: 0.8, py: 0.2, borderRadius: 1, fontSize: '0.85rem', fontFamily: 'monospace' },
              '& pre': { backgroundColor: '#f8fafc', p: 2, borderRadius: 1, overflow: 'auto', border: '1px solid #e2e8f0' },
              '& pre code': { backgroundColor: 'transparent', p: 0 },
              '& blockquote': { borderLeft: `3px solid ${COLORS.teal}`, pl: 2, ml: 0, color: COLORS.textSecondary, fontStyle: 'italic' },
              '& hr': { border: 'none', borderTop: '1px solid #e2e8f0', my: 2 },
              '& table': { width: '100%', borderCollapse: 'collapse', mb: 2 },
              '& th, & td': { border: '1px solid #e2e8f0', p: 1, fontSize: '0.9rem' },
              '& th': { backgroundColor: COLORS.backgroundLight, fontWeight: 600 },
            }}>
              <ReactMarkdown>{result.proposal_draft}</ReactMarkdown>
            </Box>
          )}

          {/* Boton de descarga */}
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadWord}
              fullWidth
              sx={{
                backgroundColor: '#2b579a',
                '&:hover': {
                  backgroundColor: '#1e3f6f',
                },
              }}
            >
              Descargar Word
            </Button>
          </Box>

          <Alert severity="info">
            Este borrador es una estructura base generada automáticamente. Revisa y completa cada sección
            con el contenido técnico de tu empresa antes de presentar la oferta.
          </Alert>
        </Paper>
      )}

      {/* Informacion adicional */}
      {!result && !isProcessing && (
        <Paper sx={{ p: 3, backgroundColor: COLORS.backgroundLight }}>
          <Typography variant="h6" gutterBottom>
            Información del Generador de Ofertas
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Sube los <strong>PDFs del pliego de la licitación</strong> (PCAP, PPT, Anexos) y obtendrás el borrador de tu oferta:
          </Typography>
          <ul style={{ marginLeft: 20 }}>
            <li>
              <Typography variant="body2" color="text.secondary">
                <strong>Detección de Índice:</strong> Identifica los criterios de adjudicación y su ponderación
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                <strong>Estructura de Propuesta:</strong> Secciones que debe contener la memoria técnica
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                <strong>Requisitos de Formato:</strong> Fuente, márgenes, páginas máximas y otras restricciones
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                <strong>Análisis por Secciones:</strong> Resumen de qué escribir en cada apartado
              </Typography>
            </li>
          </ul>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
            El generador analiza los pliegos y crea un borrador de oferta en Word que puedes
            descargar y completar con el contenido técnico de tu empresa.
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default GenerarPropuesta;
